import React from 'react';
import { 
  DollarSign, Users, Clock, Shield, CheckCircle, ArrowRight, 
  Phone, Zap, TrendingUp, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';

const ContractorHome = () => {
  const benefits = [
    {
      icon: Zap,
      title: 'Exclusive Leads',
      description: 'First to claim gets exclusive access. No sharing leads with competitors.'
    },
    {
      icon: DollarSign,
      title: 'Pay Per Lead',
      description: 'Only pay for leads you claim. No monthly fees or contracts.'
    },
    {
      icon: Users,
      title: 'Qualified Customers',
      description: 'Homeowners actively seeking HVAC services in your area.'
    },
    {
      icon: Clock,
      title: 'Real-Time Alerts',
      description: 'Get notified instantly when new leads come in.'
    },
    {
      icon: Shield,
      title: 'Lead Guarantee',
      description: 'Bad lead? Get a credit within 48 hours. No questions asked.'
    },
    {
      icon: TrendingUp,
      title: 'Grow Your Business',
      description: 'Scale up or down based on your capacity. You\'re in control.'
    }
  ];

  const pricing = [
    { service: 'Maintenance', price: '$25' },
    { service: 'Duct Cleaning', price: '$30' },
    { service: 'AC/Heating Repair', price: '$35' },
    { service: 'AC/Heating Installation', price: '$75' },
    { service: 'Emergency Service', price: '$85' },
  ];

  const steps = [
    { step: '1', title: 'Register', desc: 'Create your free account in 2 minutes' },
    { step: '2', title: 'Add Funds', desc: 'Load your account with $100-$1,000' },
    { step: '3', title: 'Claim Leads', desc: 'See new leads and claim the ones you want' },
    { step: '4', title: 'Close Deals', desc: 'Contact customers and win the job' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0EA5E9] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              NOLA HVAC Connect
            </span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/contractors/portal" className="text-slate-600 hover:text-[#0F172A] font-medium">
              Log In
            </a>
            <a
              href="/contractors/portal"
              className="bg-[#F97316] text-white px-6 py-2 rounded-full font-bold hover:bg-[#EA580C] transition-colors"
            >
              Get Started
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-[#0F172A] to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-6">
              <Star className="w-4 h-4 text-[#F97316]" />
              <span>Trusted by 50+ HVAC contractors in Louisiana</span>
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Get More HVAC Jobs
              <br />
              <span className="text-[#F97316]">Without the Hassle</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl">
              We connect you with homeowners who need HVAC services right now. 
              No cold calling. No door knocking. Just qualified leads delivered to your dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contractors/portal"
                className="inline-flex items-center justify-center gap-2 bg-[#F97316] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#EA580C] transition-colors"
              >
                Start Getting Leads
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="/demo"
                className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-colors"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              How It Works
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Start receiving qualified HVAC leads in less than 5 minutes
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((item, index) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-[#0EA5E9] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{item.title}</h3>
                <p className="text-slate-500">{item.desc}</p>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-slate-300 mx-auto mt-4 hidden md:block rotate-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Contractors Choose Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="bg-white p-6 rounded-2xl border border-slate-100 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{benefit.title}</h3>
                <p className="text-slate-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Transparent Pricing
              </h2>
              <p className="text-slate-500 mb-6">
                Pay only for the leads you claim. No monthly fees, no contracts, no hidden charges.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  No monthly subscription
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  No long-term contracts
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Bad lead guarantee
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Funds never expire
                </li>
              </ul>
              <a
                href="/contractors/portal"
                className="inline-flex items-center gap-2 bg-[#F97316] text-white px-8 py-4 rounded-full font-bold hover:bg-[#EA580C] transition-colors"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-slate-200">
              <h3 className="text-xl font-bold text-[#0F172A] mb-6">Lead Pricing</h3>
              <div className="space-y-4">
                {pricing.map((item) => (
                  <div key={item.service} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <span className="text-slate-600">{item.service}</span>
                    <span className="text-xl font-bold text-[#0F172A]">{item.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4">
                * Prices reflect lead value and market demand. Emergency and installation leads have higher conversion rates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Grow Your Business?
          </h2>
          <p className="text-slate-300 mb-8">
            Join 50+ HVAC contractors already getting leads through NOLA HVAC Connect.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contractors/portal"
              className="inline-flex items-center justify-center gap-2 bg-[#F97316] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#EA580C] transition-colors"
            >
              Create Free Account
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="/demo"
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-colors"
            >
              Watch Demo
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContractorHome;
